import { AccountCircle, CheckCircle } from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Container,
  Grid,
  ThemeProvider,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ContentComponent,
  ContentElement,
  ImagesElement,
  MusicSetElement,
  NFTElement,
  NFTType,
} from "../data/bll";
import { Cvt } from "../data/dto";
import { getEventData, searchParamsToCvt, verifyCvt } from "../logic/Cvt";
import { blackTheme } from "../shared:0.0.2/themes/blackTheme";
import ImageViewer from "./contentElements/ImageViewer";
import NFTViewer from "./contentElements/NFTViewer";
import SoundPlayer from "./contentElements/SoundPlayer";
import LoadingScreen from "./lib/LoadingScreen";
import Discount from "./contentElements/Discount";
import { handleGAButtonClick } from "../shared:0.0.2/analytics/GA";
import { EventContext } from "../shared:0.0.2/frame/EventContextProvider";
import { EventData } from "../shared:0.0.2/types/dto";

export const VerifyOracle = () => {
  const [contentElements, setContentElements] = React.useState<
    ContentElement[]
  >([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cvt, setVerifyCvt] = React.useState<Cvt>();
  const [eventData, setEventData] = React.useState<EventData>()!;
  const [valid, setValid] = React.useState<boolean>();
  let footerContext = useContext(EventContext);

  useEffect(() => {
    if (cvt) {
      fetch(
        process.env.REACT_APP_STATIC_SERVER +
          "/" +
          cvt.smartContractAddress +
          "/backstage/contentElements.json"
      )
        .then((response) => response.json())
        .then((data) => {
          setContentElements(data);
        });
    }
  }, [cvt]);

  React.useEffect(() => {
    if (eventData && footerContext) {
      footerContext.setEvent(eventData);
    }
  }, [eventData]);

  React.useEffect(() => {
    let _Cvt = searchParamsToCvt(searchParams);
    if (_Cvt) {
      setVerifyCvt(_Cvt);
    } else {
      navigate("/error", { state: { statusCode: 400 } });
    }
  }, [searchParams, navigate]);

  React.useEffect(() => {
    //TODO: ignore display when we go to invalid path, otherwise on reload this will destroy everything we are just hacking at this point
    let _cvt = searchParamsToCvt(searchParams);
    if (_cvt !== null) {
      getEventData(_cvt)
        .then((response) => {
          if (response.status !== 200) {
            throw new Error(response.status + ": " + response.statusText);
          } else {
            return response.json();
          }
        })
        .then((_eventData: EventData) => {
          setEventData(_eventData);
        })
        .catch((error) =>
          navigate("/error", {
            state: {
              statusCode: 400,
              msg: "The event information for the Ticket could not be retrieved, indicating that the Ticket is not issued by MintHarbor",
            },
          })
        );
    } else {
      navigate("/error", {
        state: {
          statusCode: 400,
          msg: "Ticket is invalid because the Ticket Information cannot be resolved",
        },
      });
    }
  }, [searchParams, navigate]);

  React.useEffect(() => {
    if (cvt) {
      verifyCvt(cvt)
        .then((res) => {
          if (!res.ok) {
            throw new Error();
          }
          return res.json();
        })
        .then((body) => {
          if (body.valid === false) {
            handleGAButtonClick("User", "Login failed");
            if (body.revert_reason) {
              navigate("/error", {
                state: {
                  statusCode: 200,
                  msg: body.revert_reason,
                  cvt: cvt,
                },
              });
            } else {
              navigate("/error", {
                state: {
                  statusCode: 200,
                  msg: "The ticket is invalid",
                  cvt: cvt,
                },
              });
            }
          }
          handleGAButtonClick("User", "Login Success");
          setValid(body.valid);
        })
        .catch((err) => {
          navigate("/error", {
            state: {
              statusCode: 500,
              msg: "The server was not able to to handle the request",
            },
          });
        });
    }
  }, [cvt]);

  if (!cvt || !eventData || contentElements.length === 0) {
    return <LoadingScreen></LoadingScreen>;
  }

  const createNFTData = (type: NFTType) => {
    return {
      type: type,
      imageUrl:
        process.env.REACT_APP_STATIC_SERVER +
        "/" +
        eventData.contractAddress +
        "/images/" +
        (type === NFTType.UNIQUE ? cvt.cvtId : 0) +
        ".png",
      smartContractAddress: eventData.contractAddress,
      cvtId: cvt.cvtId,
      jsonUrl:
        process.env.REACT_APP_STATIC_SERVER +
        "/" +
        eventData.contractAddress +
        "/json/" +
        (type === NFTType.UNIQUE ? cvt.cvtId : 0) +
        ".json",
    };
  };

  const getContentElement = ({
    component,
    data,
    exclusive,
  }: ContentElement) => {
    switch (component) {
      case ContentComponent.MUSIC_SET:
        console.log(component);
        return (
          <SoundPlayer {...(data as MusicSetElement)} exclusive={exclusive} />
        );
      case ContentComponent.IMAGES:
        return (
          <ImageViewer
            {...(data as ImagesElement)}
            exclusive={exclusive}
          ></ImageViewer>
        );
      //return <ImagesComponent data={data as ImagesElement} />;
      case ContentComponent.NFT:
        return (
          <NFTViewer
            {...createNFTData((data as NFTElement).type)}
            exclusive={exclusive}
          ></NFTViewer>
        );
      case ContentComponent.DISCOUNT:
        return (
          <Discount
            eventTitle={eventData.title}
            exclusive={exclusive}
          ></Discount>
        );
      //return <UniqueNftComponent data={data as UniqueNftElement} />;
      default:
        return null;
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        pt: { xs: 5 },
        pb: 5,
      }}
    >
      <Box sx={{ backgroundColor: "background.default" }}>
        <ThemeProvider theme={blackTheme}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <ButtonBase
              onClick={() => {
                handleGAButtonClick("User", "Clicked Account");
                navigate("/feedback");
              }}
            >
              <AccountCircle sx={{ height: 60, width: 60 }}></AccountCircle>
            </ButtonBase>
            {!valid ? (
              <Box
                sx={{
                  alignSelf: "center",
                  p: 1,
                  display: "flex",
                  flexDirection: "row",
                  backgroundColor: "background.default",
                  borderRadius: 2,
                }}
              >
                <Typography sx={{ mr: 1 }}>Logging in</Typography>
                <CircularProgress size={25}></CircularProgress>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 1,
                  display: "flex",
                  alignSelf: "center",
                  backgroundColor: "background.default",
                  flexDirection: "row",
                  borderRadius: 2,
                }}
              >
                <Typography sx={{ mr: 1 }}>
                  Logged in with Ticket#{cvt.cvtId}
                </Typography>
                <CheckCircle sx={{ height: 25 }}></CheckCircle>
              </Box>
            )}
          </Box>
        </ThemeProvider>
      </Box>
      <Typography variant="h5" mt={3}>
        Your Backstage Content for {eventData?.title}
      </Typography>
      {valid ? (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {contentElements.map((item, key) => (
            <ThemeProvider theme={blackTheme} key={key}>
              <Grid item xs={12}>
                {getContentElement(item)}
              </Grid>
            </ThemeProvider>
          ))}
        </Grid>
      ) : null}
    </Container>
  );
};
